import { useEffect, useRef, useState } from 'react'
import { useMetaData } from '../queries/useMetaData.js'
import { useUIStore } from '../store/uiStore.js'

/**
 * MentionToast — shows a toast when a new reply arrives that @-mentions
 * the current user.
 *
 * Detection:
 *   On each meta refetch, walk all comment threads. For each reply whose
 *   text contains @currentUser and whose author is not the current user,
 *   compare against a ref of previously-seen reply IDs. If it's new,
 *   trigger a toast.
 *
 * No-ops if currentUser is not set, or if meta hasn't loaded yet.
 */
export default function MentionToast() {
  const currentUser = useUIStore((s) => s.currentUser)
  const openCommentPanel = useUIStore((s) => s.openCommentPanel)
  const { data: meta } = useMetaData()

  const seenReplyIds = useRef(new Set())
  // Timestamp of when this component mounted. Any reply with an `at`
  // older than this is historical and must never trigger a toast, even
  // on the very first meta fetch after page load. Previously we tried
  // to "prime" the seen-set on first effect run, but that run often
  // happened before meta had actually resolved — so once data arrived,
  // everything looked new and old messages re-toasted every refresh.
  const firstLoadAt = useRef(Date.now())
  const [toast, setToast] = useState(null)

  useEffect(() => {
    if (!currentUser || !meta.comments) return

    const mentionPattern = new RegExp(`@${escapeRegex(currentUser)}\\b`, 'i')
    for (const thread of meta.comments) {
      for (const reply of (thread.replies || [])) {
        if (seenReplyIds.current.has(reply.id)) continue
        seenReplyIds.current.add(reply.id)

        // Skip anything created before the page loaded — it's history,
        // not a new notification.
        const replyMs = reply.at ? new Date(reply.at).getTime() : 0
        if (!replyMs || replyMs < firstLoadAt.current) continue

        if (reply.author === currentUser) continue
        if (!mentionPattern.test(reply.text || '')) continue

        setToast({
          from: reply.author,
          preview:
            (reply.text || '').length > 80
              ? (reply.text || '').slice(0, 80) + '…'
              : reply.text,
          contextLabel: thread.contextLabel,
          contextType: thread.contextType,
        })
        return // one toast at a time
      }
    }
  }, [meta.comments, currentUser])

  // Auto-dismiss after 8s
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 8000)
    return () => clearTimeout(t)
  }, [toast])

  if (!toast) return null

  return (
    <div
      onClick={() => {
        openCommentPanel({
          label: toast.contextLabel,
          type: toast.contextType || 'Context',
        })
        setToast(null)
      }}
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: 320,
        background: '#fff',
        border: '1px solid var(--border)',
        borderLeft: `4px solid #3b82f6`,
        borderRadius: 10,
        boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
        padding: '12px 14px',
        zIndex: 1000,
        cursor: 'pointer',
        animation: 'slideInRight 0.3s ease-out',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span style={{ fontSize: 14 }}>💬</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
          {toast.from} mentioned you
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation()
            setToast(null)
          }}
          style={{
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            color: 'var(--muted-light)',
            cursor: 'pointer',
            fontSize: 14,
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--muted)',
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        on: {toast.contextLabel}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          lineHeight: 1.4,
        }}
      >
        {toast.preview}
      </div>
      <div
        style={{
          fontSize: 10,
          color: 'var(--muted-light)',
          fontStyle: 'italic',
          marginTop: 6,
        }}
      >
        click to open thread
      </div>
    </div>
  )
}

function escapeRegex(s) {
  return (s || '').replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
}
